import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout, resetState } from '../GlobalAction';
import { AssetType } from '../activity/activitySlice';
import { IClientNameListItem } from '../../types/client/IClientNameListItem';

export enum SenderType {
  COACH = 'COACH',
  CLIENT = 'CLIENT',
}

export enum MessageUpdateType {
  DATA_UPDATE = 'DATA_UPDATE',
  STATUS_UPDATE = 'STATUS_UPDATE',
}

export enum ChatMessageStatus {
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  SEEN = 'SEEN',
}

export interface IChatMessageUpdatePayload {
  chatMessagesId: string;
  messageStatusInput: ChatMessageStatus;
}

export interface IChatMessage {
  id: string;
  chatId: string;
  message: string;
  messageType: AssetType;
  senderType: SenderType;
  createdAt: string;
  updatedAt: string;
  assetDuration: number;
  messageSent?: boolean;
  messageSeen?: boolean;
  messageDelivered?: boolean;
}

export interface IChatList {
  id: string;
  clientId: string;
  updatedAt: string;
  chatMessages: IChatMessage[];
  client: IClientNameListItem;
  messageUpdateType?: MessageUpdateType;
  hasNewMessages?: boolean;
}

export interface IGetChatList {
  totalRecordCount: number;
  chats: IChatList[];
}

export interface IGetChatListResponse {
  getChatList: IGetChatList;
}

export interface IGetChatMessages {
  totalMessageCount: number;
  messages: IChatMessage[];
}

export interface IGetChatMessagesResponse {
  getChatMessages: IGetChatMessages;
}

export interface ISendMessagePayload {
  clientId: string;
  coachId: string;
  message: string;
  senderType: SenderType;
  assetDuration: string;
  assetFile?: File;
  chatMessageType: AssetType;
}
export interface IGetChatListPayload {
  coachId: string;
  filterValue?: string;
  skip: number;
  take: number;
  isFiltering?: boolean;
}
export interface IGetChatMessagesPayload {
  clientId: string;
  coachId: string;
  skip: number;
  take: number;
}

export interface ISentMessage {
  message: IChatMessage;
}

export interface ISendMessageResponse {
  sendMessage: ISentMessage;
}

export interface ICoachMessagesPyalod {
  coachMessages: IChatList;
}

interface IChatState {
  getChatListLoading: boolean;
  chatList: IChatList[];
  sendMessageLoading: boolean;
  totalChatHistoryCount: number;
  fetchClientsLoading: boolean;
  getChatMessagesLoading: boolean;
  chatMessages: IChatMessage[];
  totalMessageCount: number;
  sentMessage: {
    message: IChatMessage | null;
  };
  isMessageReceived: boolean;
  hasMore: boolean;
  skip: number;
  hasMoreChatHistory: boolean;
  skipChatHistory: number;
  totalUnreadChatCount: number;
}

const initialState: IChatState = {
  getChatListLoading: false,
  chatList: [],
  sendMessageLoading: false,
  totalChatHistoryCount: 0,
  fetchClientsLoading: false,
  getChatMessagesLoading: false,
  chatMessages: [],
  totalMessageCount: 0,
  sentMessage: {
    message: null,
  },
  isMessageReceived: false,
  hasMore: true,
  skip: 0,
  hasMoreChatHistory: true,
  skipChatHistory: 0,
  totalUnreadChatCount: 0,
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    getChatList: (state, action: PayloadAction<IGetChatListPayload>) => {
      state.getChatListLoading = true;
      if (action.payload.isFiltering) {
        state.chatList = [];
        state.totalChatHistoryCount = 0;
        state.hasMoreChatHistory = true;
        state.skipChatHistory = 0;
      }
    },
    getChatListSuccess: (state, action: PayloadAction<IGetChatList>) => {
      state.getChatListLoading = false;
      state.totalChatHistoryCount = action.payload.totalRecordCount;

      // use map to avoid duplicates
      const chatHashMap = new Map<string, IChatList>();
      for (let i = 0; i < state.chatList.length; i += 1) {
        const chat = state.chatList[i];
        chatHashMap.set(chat.id, chat);
      }

      // update with latest data
      for (let i = 0; i < action.payload.chats.length; i += 1) {
        const chat = action.payload.chats[i];
        chatHashMap.set(chat.id, chat);
      }

      state.chatList = Array.from(chatHashMap.values())
        .map((chat) => ({
          ...chat,
          hasNewMessages: chat.chatMessages.some(
            (message) => !message.messageSeen && message.senderType === SenderType.CLIENT
          ),
        }))
        .sort((a, b) => Number(b.hasNewMessages) - Number(a.hasNewMessages));
    },
    getChatListFailure: (state) => {
      state.getChatListLoading = false;
    },
    clearChatList: (state) => {
      state.chatList = [];
      state.totalChatHistoryCount = 0;
    },
    sendMessage: (state, action: PayloadAction<ISendMessagePayload>) => {
      if (action.payload.assetFile) {
        state.sendMessageLoading = true;
      }
    },
    sendMessageSuccess: (state, action: PayloadAction<ISentMessage>) => {
      state.sendMessageLoading = false;
      state.sentMessage = action.payload;
    },
    sendMessageFailure: (state) => {
      state.sendMessageLoading = false;
    },
    getChatMessages: (state, _action: PayloadAction<IGetChatMessagesPayload>) => {
      state.getChatMessagesLoading = true;
    },
    getChatMessagesSuccess: (state, action: PayloadAction<IGetChatMessages>) => {
      state.getChatMessagesLoading = false;
      state.chatMessages = [...state.chatMessages, ...action.payload.messages];
      state.totalMessageCount = action.payload.totalMessageCount;
    },
    getChatMessagesFailure: (state) => {
      state.getChatMessagesLoading = false;
    },
    clearChatMessages: (state) => {
      state.chatMessages = [];
      state.totalMessageCount = 0;
    },
    setChatNotificationStatus: (state, action: PayloadAction<boolean>) => {
      state.isMessageReceived = action.payload;
    },
    setHasMoreMessages: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setMessagesSkip: (state, action: PayloadAction<number>) => {
      state.skip = action.payload;
    },
    setHasMoreChatHistory: (state, action: PayloadAction<boolean>) => {
      state.hasMoreChatHistory = action.payload;
    },
    setChatHistorySkip: (state, action: PayloadAction<number>) => {
      state.skipChatHistory = action.payload;
    },
    updateMessageStatus: (state, _action: PayloadAction<IChatMessageUpdatePayload>) => state,
    updateChatCount: (state, action: PayloadAction<number>) => {
      state.totalUnreadChatCount = action.payload;
    },
    updateChat: (state, action: PayloadAction<IChatList>) => {
      const { id, chatMessages, messageUpdateType } = action.payload;

      let chat = state.chatList.find((c) => c.id === id);
      if (!chat) {
        chat = { ...action.payload, hasNewMessages: true };
        state.chatList.push(chat);
      }

      if (messageUpdateType === MessageUpdateType.STATUS_UPDATE) {
        const updatedMessage = chatMessages[0];
        chat.chatMessages = chat.chatMessages.map((message) =>
          message.id === updatedMessage.id ? updatedMessage : message
        );
      } else {
        state.totalUnreadChatCount += 1;
        chat.hasNewMessages = true;
        chat.chatMessages = [...chat.chatMessages, chatMessages[0]];
      }
      state.chatList.sort((a, b) => Number(b.hasNewMessages) - Number(a.hasNewMessages));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetState, () => initialState);
    builder.addCase(logout, () => initialState);
  },
});

export const {
  getChatList,
  getChatListSuccess,
  getChatListFailure,
  sendMessage,
  sendMessageSuccess,
  sendMessageFailure,
  clearChatList,
  getChatMessages,
  getChatMessagesSuccess,
  getChatMessagesFailure,
  clearChatMessages,
  setChatNotificationStatus,
  setHasMoreMessages,
  setMessagesSkip,
  setHasMoreChatHistory,
  setChatHistorySkip,
  updateMessageStatus,
  updateChatCount,
  updateChat,
} = chatSlice.actions;

export default chatSlice.reducer;
